<script setup lang="ts">
import type { Article } from "~/types/article"

const props = defineProps({
  data: {
    type: Object as PropType<Article>,
    default: () => {},
  },
})

const imageSize = computed(() => {
  switch (props.data.imageSize) {
    case "Small":
      return 4

    case "Medium":
      return 6

    case "Large":
    default:
      return 8
  }
})

const textClasses = computed(() => {
  const size =
    imageSize.value <= 4 ? imageSize.value + 1 : imageSize.value
  const start = size + 2
  const end = 18

  return `col-start-2 col-end-18 @md:col-start-${start} @md:col-end-${end} @lg:col-start-${
    start + 1
  } @lg:col-end-${end - 1} @2xl:col-start-${
    start + 1
  } @2xl:col-end-${end}`
})

const imageClasses = computed(() => {
  const end = imageSize.value + 2

  return `col-start-1 col-end-19 @md:col-start-1 @md:col-end-${end}`
})

defineExpose({
  SIDENOTE_OFFSET: 1,
})

const assignmentType = computed(
  () => props.data.mathTask?.assignmentType,
)
</script>

<template>
  <UArticle>
    <UArticleHeader
      :data="data"
      class="order-1 h-fit break-words pt-[var(--grid-col-width)] @md:order-none @md:row-start-1"
      :class="textClasses"
    >
      <template #contentTag>
        <UContentBadge
          v-if="assignmentType"
          :content="assignmentType"
          :aria-label="`Oppgavetype: ${assignmentType}`"
          passive
          :icon="
            assignmentType === 'Mengdetrening' ? 'repeat' : undefined
          "
        />
      </template>
    </UArticleHeader>

    <u-article-image
      :data="data"
      class="u-article__image order-2 @md:sticky @md:top-0 @md:order-none @md:row-span-5"
      :class="imageClasses"
      :default-picture-frame-colors="true"
    />

    <div
      class="order-3 flex h-fit flex-col gap-8 rounded-lg bg-white px-8 pt-8 @md:order-none @md:row-start-2 lg:rounded-2xl lg:p-12"
      :class="textClasses"
    >
      <section
        class="flex flex-col gap-8 break-words text-[18px] md:text-[24px] [&>*]:font-national2"
      >
        <h2
          v-if="data.intro?.html || data.body?.html"
          class="sr-only"
        >
          Oppgaveintroduksjon
        </h2>
        <u-rich-text
          class="text-mobile-math-article-intro md:text-desktop-math-article-intro"
          :class="textClasses"
          :html="data.intro?.html"
        />
        <u-rich-text v-if="data.body" :html="data.body.html" />
        <h2 class="sr-only">Oppgaver</h2>
        <UMathTaskAssignments v-if="data.mathTask" />
      </section>
      <UMathTaskActions v-if="data.mathTask" />
    </div>
    <UArticleContentFooter
      :class="textClasses"
    />
  </UArticle>
</template>
